// Resolves which administrative level a just-logged-in user belongs to.
//
// The backend gives no hint at login time (POST /auth/login returns only
// { status, data: userId, moniterd_ttl, loginTime } — no role/level). The only way
// to learn the level is to call a whoami endpoint, and each whoami endpoint's
// middleware rejects (401) any role that doesn't match its level — v5/cluster
// authorisationCluster requires role === "Cluster User", v2/district requires
// role === "Admin" + ACCESS_DISTRICT_DATA, etc. So we probe in sequence and use
// whichever one succeeds; its response body already contains `level` untouched
// (userFindById only strips role/email/_id/isDeleted/sessionID/token).
//
// This lets one login form serve all 4 levels without backend changes.

import { API_BASE_URL } from "./env";
import { LEVEL_CONFIG, LEVEL_PROBE_ORDER, Level } from "./levelConfig";

export interface WhoamiResult {
  level: Level;
  user: Record<string, any>;
}

async function tryWhoami(
  authApiVersion: number,
  userId: string,
  moniterdTtl: string
): Promise<Record<string, any> | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/v${authApiVersion}/${userId}/${moniterdTtl}`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) return null;
    const body = await response.json();
    if (body?.status !== "Success") return null;
    return body?.data ?? null;
  } catch {
    return null;
  }
}

/**
 * Probes whoami endpoints in LEVEL_PROBE_ORDER until one succeeds, returning the
 * matched level and the (level-bearing) user profile. Throws if none succeed.
 */
export async function resolveUserLevel(userId: string, moniterdTtl: string): Promise<WhoamiResult> {
  for (const level of LEVEL_PROBE_ORDER) {
    const { authApiVersion } = LEVEL_CONFIG[level];
    const user = await tryWhoami(authApiVersion, userId, moniterdTtl);
    if (user) {
      // Trust the level the backend actually reports, in case it ever
      // disagrees with which probe happened to succeed first.
      const resolvedLevel: Level = (user.level as Level) || level;
      return { level: resolvedLevel, user };
    }
  }
  throw new Error("Could not determine account level — none of the whoami endpoints accepted this session.");
}
